// Business rule: from summer 2026 onward, non-Fleet vendors have a single
// combined July-August objective (stored under month=7 with period="jul-aug").
export const SUMMER_MERGE_YEAR = 2026;

export const MONTH_NAMES_LONG = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export const MONTH_NAMES_SHORT = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];

export const isConcessionFleet = (concession) => {
  if (!concession) return false;
  return (
    concession.is_fleet === true ||
    concession.objective_type === "quarterly" ||
    (concession.name || "").toUpperCase() === "FLEET"
  );
};

/**
 * Whether the given (month, year) belongs to the merged Jul-Aug summer period
 * for the concession (non-Fleet, year >= SUMMER_MERGE_YEAR, month in {7,8}).
 */
export const isSummerMerged = (month, year, concession) => {
  const isFleet = isConcessionFleet(concession);
  return !isFleet && Number(year) >= SUMMER_MERGE_YEAR && (month === 7 || month === 8);
};

/**
 * The canonical month used to store objectives / read data for a given month.
 * For merged summer period (non-Fleet, year>=2026, month in {7,8}) -> 7.
 */
export const canonicalMonth = (month, year, concession) => {
  return isSummerMerged(month, year, concession) ? 7 : month;
};

/**
 * Human-readable label of a period.
 * Returns "Juillet-Août YYYY" when the objective/period is merged.
 */
export const periodLabel = (month, year, concession, { longLabel = true } = {}) => {
  const monthList = longLabel ? MONTH_NAMES_LONG : MONTH_NAMES_SHORT;
  if (isSummerMerged(month, year, concession)) {
    return longLabel ? `Juillet-Août ${year}` : `Juil-Août ${year}`;
  }
  const idx = Math.max(1, Math.min(12, Number(month) || 1)) - 1;
  return `${monthList[idx]} ${year}`;
};

/**
 * The list of month numbers to iterate over for a given concession/year context.
 * When merged, returns [1..12] EXCEPT 8 (August absorbed into July).
 */
export const monthsForContext = (year, concession) => {
  if (isConcessionFleet(concession)) return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  if (Number(year) >= SUMMER_MERGE_YEAR) return [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12];
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
};

/**
 * Label of a single month, applying summer merge display for month 7 when relevant.
 */
export const monthLabel = (month, year, concession, { longLabel = true } = {}) => {
  const monthList = longLabel ? MONTH_NAMES_LONG : MONTH_NAMES_SHORT;
  if (
    month === 7 &&
    !isConcessionFleet(concession) &&
    Number(year) >= SUMMER_MERGE_YEAR
  ) {
    return longLabel ? "Juillet-Août" : "Juil-Août";
  }
  const idx = Math.max(1, Math.min(12, Number(month) || 1)) - 1;
  return monthList[idx];
};
